import { Component, Input, OnInit } from '@angular/core';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { LLCTransaction } from '@models/llc-mortgages';
import {
	BusinessMatterClient,
	BusinessMatterClientWithAssociations,
	Matter,
	MatterAdjustment,
	MatterTitle,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { MatterMortgage } from '@models/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { MatterClients } from '@state/global/matter_clients';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import {
	StepMilestone,
	Step,
	StepMilestoneStatus,
} from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';
import {
	getLLCLenderType,
	LLCLenderType,
} from 'src/app/shared/services/mortgages/llc.service';
import {
	StepperItems,
	StepperSectionsBuilderService,
} from './stepper-sections-builder.service';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Jurisdiction } from '@models/jurisdictions';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';
import { LLCMilestoneName } from '@models/mortgages/mortgage-base';

const stepNames = {
	[LLCMilestoneName.ACCEPTED]: 'Accepted',
	[LLCMilestoneName.FINAL]: 'Final report',
	[LLCMilestoneName.BROKER_SATISFIED]: 'Broker conditions',
	[LLCMilestoneName.DEAL_FUNDED]: 'Deal funded',
	[LLCMilestoneName.LENDER_AUTH_TO_FUND]: 'Lender authorized',
	[LLCMilestoneName.SOLICITOR_SATISFIED]: 'Solicitor conditions',
	[LLCMilestoneName.PRELIMINARY]: 'Request for funds',
};
@UntilDestroy()
@Component({
	selector: 'app-llc-stepper',
	templateUrl: './llc-stepper.component.html',
	styleUrls: ['./llc-stepper.component.scss'],
	standalone: false,
})
export class LlcStepperComponent implements OnInit {
	public steps: Step[] = [];
	public currentStep = 0;
	public _mortgage: LLCTransaction;
	public _matterMortgage: MatterMortgage;
	public clients: (PersonMatterClient | BusinessMatterClient)[] = [];

	public adjustments: MatterAdjustment[];
	public matter: Matter;
	public titleInsuranceEffectiveDate: string;
	public primaryJuridiction: Jurisdiction;
	public clientAssociations: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[];
	public guarantors: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[];

	@Select(AdjustmentsSelector.getAll) adjustments$: Observable<
		MatterAdjustment[]
	>;
	@Select(SelectedMatter.getSelected) matter$: Observable<Matter>;
	@Select(SelectedMatter.titleInsuranceEffectiveDate)
	titleInsuranceEffectiveDate$: Observable<string>;
	@Select(MatterClients.get_matterClientsWithAssociationMapped)
	clientsAssociationsMapped$: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;
	@Select(MatterClients.get_guarantorsWithAssociationMapped)
	guarantosAssociationsMapped$: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;
	@Select(MatterJurisdictionSelectors.getPrimary)
	primaryJurisdiction$: Observable<Jurisdiction>;

	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;
	@SelectSnapshot(MatterTitlesState.getAll) matterTitles: MatterTitle[];

	@Input() set matterMortgage(matterMortgage: MatterMortgage) {
		this._matterMortgage = matterMortgage;
		if (matterMortgage) {
			this.steps = this.buildSteps(
				this._mortgage,
				this.matter,
				this._matterMortgage,
				this.clientAssociations,
				this.guarantors,
				this.matterTitles,
				this.nameFormattingSettings,
				this.titleInsuranceEffectiveDate,
				this.primaryJuridiction,
			);
		}
	}

	@Input() set mortgage(mortgage: LLCTransaction) {
		this._mortgage = mortgage;
		if (mortgage) {
			this.steps = this.buildSteps(
				this._mortgage,
				this.matter,
				this._matterMortgage,
				this.clientAssociations,
				this.guarantors,
				this.matterTitles,
				this.nameFormattingSettings,
				this.titleInsuranceEffectiveDate,
				this.primaryJuridiction,
			);
		}
	}

	constructor(private stepperBuildService: StepperSectionsBuilderService) {}

	ngOnInit(): void {
		combineLatest([
			this.matter$,
			this.clientsAssociationsMapped$,
			this.guarantosAssociationsMapped$,
			this.adjustments$,
			this.titleInsuranceEffectiveDate$,
			this.primaryJurisdiction$,
		])
			.pipe(untilDestroyed(this))
			.subscribe(
				([
					_matter,
					_clientAssociations,
					_guarantors,
					adjustments,
					_titleInsuranceEffectiveDate,
					_primaryJuridisction,
				]) => {
					this.matter = _matter;
					this.clientAssociations = _clientAssociations;
					this.guarantors = _guarantors;
					this.adjustments = adjustments;
					this.titleInsuranceEffectiveDate = _titleInsuranceEffectiveDate;
					this.primaryJuridiction = _primaryJuridisction;

					this.steps = this.buildSteps(
						this._mortgage,
						this.matter,
						this._matterMortgage,
						this.clientAssociations,
						this.guarantors,
						this.matterTitles,
						this.nameFormattingSettings,
						this.titleInsuranceEffectiveDate,
						this.primaryJuridiction,
					);
				},
			);
	}

	buildSteps(
		mortgage: LLCTransaction,
		matter: Matter,
		matterMortgage: MatterMortgage,
		clientsAssociationsMapped: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		guarantors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		matterTitles: MatterTitle[],
		nameFormattingSettings: NameFormattingSettings,
		titleInsuranceEffectiveDate: string,
		jurisdiction: Jurisdiction,
	): Step[] {
		const _steps: Step[] = [];
		const milestones = mortgage?.milestones;

		if (milestones && mortgage && matterMortgage) {
			const _milestones = cloneDeep(milestones);
			const itemSections = this.getItemSections(
				mortgage,
				matter,
				matterMortgage,
				clientsAssociationsMapped,
				guarantors,
				matterTitles,
				nameFormattingSettings,
				titleInsuranceEffectiveDate,
				jurisdiction,
			);

			const ordered = _milestones.sort(
				(a, b) => Number(a.sequence) - Number(b.sequence),
			);
			let currStep = 0;

			ordered.forEach((_order, index) => {
				const nextStep =
					index + 1 < ordered?.length - 1 ? ordered[index + 1] : null;
				const failed =
					!_order.completedDateTime &&
					nextStep &&
					nextStep?.completedDateTime?.length > 0;

				if (_order.completedDateTime?.length > 0) {
					currStep = index;
				}
				const stepMilestones = this.getStepItemSection(
					_order.name,
					itemSections,
				);

				let stepmileStoneStatus: StepMilestoneStatus;

				if (stepMilestones) {
					stepmileStoneStatus = {
						total: stepMilestones?.length,
						completed: stepMilestones?.filter((x) => x?.completed)?.length ?? 0,
						milestones: stepMilestones,
					};
				}

				const _step: Step = {
					title: stepNames[_order.name],
					milestoneStatus: stepmileStoneStatus,
					failed,
				};

				_steps.push(_step);
			});
			this.currentStep = currStep;
		}

		return _steps;
	}

	getStepItemSection(
		step: LLCMilestoneName,
		itemSections: StepperItems,
	): StepMilestone[] | null {
		if (!itemSections) {
			return undefined;
		}

		switch (step) {
			case LLCMilestoneName.ACCEPTED:
				return itemSections.step0ItemSections;
			case LLCMilestoneName.PRELIMINARY:
				return itemSections.step1ItemSections;
			case LLCMilestoneName.FINAL:
				return itemSections.step2ItemSections;
			default:
				return undefined;
		}
	}

	getItemSections(
		mortgage: LLCTransaction,
		matter: Matter,
		matterMortgage: MatterMortgage,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		guarantors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		matterTitles: MatterTitle[],
		nameFormattingSettings: NameFormattingSettings,
		titleInsuranceEffectiveDate: string,
		jurisdiction: Jurisdiction,
	): StepperItems {
		let result: StepperItems;
		if (matter && mortgage) {
			if (getLLCLenderType(mortgage?.lender) === LLCLenderType.BNS) {
				result = this.stepperBuildService.getBNSSections(
					mortgage,
					matter,
					matterMortgage,
					clientAssociations,
					guarantors,
					matterTitles,
					nameFormattingSettings,
					titleInsuranceEffectiveDate,
					jurisdiction,
				);
			} else if (getLLCLenderType(mortgage.lender) === LLCLenderType.TD) {
				result = this.stepperBuildService.getTDSections(
					mortgage,
					matter,
					this.adjustments,
					matterMortgage,
					clientAssociations,
					guarantors,
					matterTitles,
					nameFormattingSettings,
					titleInsuranceEffectiveDate,
					jurisdiction,
				);
			} else {
				result = this.stepperBuildService.getMMSSections(
					mortgage,
					matter,
					this.adjustments,
					matterMortgage,
					clientAssociations,
					guarantors,
					matterTitles,
					nameFormattingSettings,
					jurisdiction,
				);
			}
		}

		return result;
	}
}
